import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, CheckCircle2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card'
import { cn } from '../../utils/cn'
import api from '../../services/api'

const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
})

type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>

interface ForgotPasswordFormProps {
  className?: string
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ className }) => {
  const [submitted, setSubmitted] = useState(false)
  const [globalError, setGlobalError] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onBlur',
    defaultValues: { email: '' },
  })

  const onSubmit = async (data: ForgotPasswordData) => {
    setGlobalError('')
    try {
      await api.post('/auth/forgot-password', { email: data.email })
      setSubmitted(true)
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Something went wrong'
      if (msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('too many')) {
        setGlobalError('Too many attempts. Please wait a few minutes before trying again.')
      } else {
        // Always show success to prevent user enumeration
        setSubmitted(true)
      }
    }
  }

  if (submitted) {
    return (
      <Card className={cn('w-full max-w-md mx-auto', className)}>
        <CardContent className="pt-8 text-center space-y-4">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
          <h2 className="text-xl font-semibold text-foreground">Check your email</h2>
          <p className="text-muted-foreground">
            If an account with that email exists, we've sent a password reset link. Please check your inbox and spam folder.
          </p>
          <Link
            to="/auth/login"
            className="inline-block text-sm text-primary hover:text-primary/80"
          >
            Back to login
          </Link>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={cn('w-full max-w-md mx-auto', className)}>
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold text-foreground">
          Forgot your password?
        </CardTitle>
        <CardDescription>
          Enter your email and we'll send you a reset link.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {globalError && (
            <div
              className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg"
              role="alert"
            >
              {globalError}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-foreground">
              Email address
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              disabled={isSubmitting}
              error={errors.email?.message}
              startIcon={<Mail className="h-4 w-4" />}
              placeholder="Enter your email"
              {...register('email')}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full rounded-full bg-gradient-to-r from-primary to-emerald-500 text-white"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send reset link'}
          </Button>

          <div className="text-center">
            <Link
              to="/auth/login"
              className="text-sm text-primary hover:text-primary/80"
            >
              Back to login
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default ForgotPasswordForm
